/**
 * DETERMINISTIC RECOVERY ENGINE — the only place a recovery dollar may be born.
 *
 * Pipeline (pure, no network, no AI, no randomness):
 *
 *   Canonical Invoice
 *        ↓  buildChargeInventory()          (charge-inventory.ts)
 *   Charge Inventory
 *        ↓  resolveChargeBenchmarkStatuses() (charge-benchmark-status.ts)
 *   Benchmark Status Resolution
 *        ↓  recovery eligibility rules       (this module)
 *   Deterministic Charge Recovery
 *        ↓  Σ eligible contributions
 *   Canonical Supported Recovery
 *
 * Rules (RECOVERY_CALCULATION_VERSION v1.0):
 *   • Only charges inside the CERTIFIED SUPPORTED SCOPE (a resolved benchmark
 *     exists for the charge) may contribute dollars. The scope total is the
 *     identical identity Chapter 02 publishes:
 *        Σ(vendor amount) − Σ(supported amount) over the supported scope.
 *   • Charges with no benchmark, or held for vendor duplicate clarification,
 *     contribute exactly $0 — never an inferred, averaged or percentage figure.
 *   • Every contributing dollar carries lineage back to the invoice line.
 *   • AI output is NEVER an input to this module.
 *
 * Identical canonical inputs always produce byte-identical output.
 */

import {
  resolveChargeBenchmarkStatuses,
  type CanonicalBenchmarkStatus,
  type CanonicalChargeStatus,
  type ChargeBenchmarkInputs,
} from "./charge-benchmark-status";

/** Deterministic rule-set identity persisted with every calculated recovery. */
export const RECOVERY_CALCULATION_VERSION = "v1.0";

/** Explicit, auditable recovery disposition for a single charge. */
export type ChargeRecoveryStatus =
  | "supported_recovery"
  | "market_difference_only"
  | "additional_evidence_required"
  | "vendor_clarification_required"
  | "legally_unsupported"
  | "duplicate_charge"
  | "no_recovery_supported";

export const CHARGE_RECOVERY_STATUS_LABEL: Record<ChargeRecoveryStatus, string> = {
  supported_recovery: "Supported Recovery",
  market_difference_only: "Market Difference Only",
  additional_evidence_required: "Additional Evidence Required",
  vendor_clarification_required: "Vendor Clarification Required",
  legally_unsupported: "Legally Unsupported",
  duplicate_charge: "Duplicate Charge",
  no_recovery_supported: "No Recovery Supported",
};

/**
 * Statuses that may move dollars. Everything else contributes exactly zero.
 * `market_difference_only` and `no_recovery_supported` remain inside the
 * certified scope because the published identity nets the whole supported
 * scope; they can only ever reduce (never inflate) the recovery figure.
 */
const ELIGIBLE_STATUSES: ReadonlySet<ChargeRecoveryStatus> = new Set<ChargeRecoveryStatus>([
  "supported_recovery",
  "market_difference_only",
  "no_recovery_supported",
]);

export function isRecoveryEligible(status: ChargeRecoveryStatus): boolean {
  return ELIGIBLE_STATUSES.has(status);
}

/** Maps the canonical benchmark status onto its recovery disposition. */
export function recoveryStatusForCharge(charge: CanonicalChargeStatus): ChargeRecoveryStatus {
  if (charge.charge.duplicateSuspect) return "duplicate_charge";
  const benchmark: CanonicalBenchmarkStatus = charge.status;
  if (benchmark === "clarification") return "vendor_clarification_required";
  if (benchmark === "pending") return "additional_evidence_required";
  if (benchmark === "materially-above" || benchmark === "above") return "supported_recovery";
  if (benchmark === "within") return "market_difference_only";
  return "no_recovery_supported"; // below market
}

/** One traceable recovery line. Every dollar in the total appears here. */
export interface ChargeRecoveryLine {
  /** Invoice line identity (charge inventory id). */
  chargeId: string;
  /** Printed charge description, exactly as billed. */
  printedDescription: string;
  normalizedChargeKey: string;
  quantity: number;
  /** Vendor amount printed on the invoice. */
  invoiceAmount: number | null;
  /** Selected benchmark value (per unit) and its documented bounds. */
  benchmarkAverage: number | null;
  benchmarkLow: number | null;
  benchmarkHigh: number | null;
  /** Supported (market) amount for this line = benchmark × this line's qty. */
  supportedAmount: number | null;
  benchmarkStatus: CanonicalBenchmarkStatus;
  recoveryStatus: ChargeRecoveryStatus;
  eligible: boolean;
  /** Where the benchmark came from (verified / researched / reused / none). */
  evidenceBasis: CanonicalChargeStatus["evidenceBasis"];
  /** Human-readable formula for the contributed amount. */
  formula: string;
  /** Dollars this line contributes to Supported Recovery. */
  recoveryAmount: number;
}

export interface DeterministicRecoveryResult {
  /** Canonical Supported Recovery in dollars (never negative). */
  supportedRecovery: number;
  /** Vendor dollars inside the certified supported scope. */
  supportedVendorTotal: number;
  /** Supported (market) dollars for the same scope. */
  supportedExpectedTotal: number;
  /** Invoice total actually used for clamping / fair payment. */
  invoiceTotal: number | null;
  /** Estimated Fair Payment = Invoice Total − Supported Recovery. */
  fairPayment: number | null;
  fairPaymentLow: number | null;
  fairPaymentHigh: number | null;
  /** Charge-level lineage, in canonical presentation order. */
  lines: ChargeRecoveryLine[];
  counts: {
    reviewed: number;
    eligible: number;
    supportedRecovery: number;
    additionalEvidenceRequired: number;
    vendorClarificationRequired: number;
  };
  /** True when no charge inventory exists (nothing to certify). */
  empty: boolean;
  calculationBasis: string;
  calculationVersion: string;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function clampMoney(v: number, total: number | null): number {
  const rounded = Math.max(0, Math.round(v));
  return total !== null ? Math.min(rounded, Math.round(total)) : rounded;
}

export interface DeterministicRecoveryInputs extends ChargeBenchmarkInputs {
  /** Printed invoice total, used only for clamping and fair payment. */
  invoiceTotal?: number | null;
}

export function computeDeterministicRecovery(
  inputs: DeterministicRecoveryInputs,
): DeterministicRecoveryResult {
  const resolution = resolveChargeBenchmarkStatuses(inputs);
  const invoiceTotal =
    typeof inputs.invoiceTotal === "number" &&
    Number.isFinite(inputs.invoiceTotal) &&
    inputs.invoiceTotal > 0
      ? inputs.invoiceTotal
      : null;

  const lines: ChargeRecoveryLine[] = resolution.statuses.map((s) => {
    const recoveryStatus = recoveryStatusForCharge(s);
    const eligible = isRecoveryEligible(recoveryStatus) && s.expectedAmount !== null;
    const invoiceAmount = s.invoiceAmount;
    const contribution =
      eligible && invoiceAmount !== null && s.expectedAmount !== null
        ? round2(invoiceAmount - s.expectedAmount)
        : 0;
    return {
      chargeId: s.chargeId,
      printedDescription: s.charge.description,
      normalizedChargeKey: s.charge.normalized,
      quantity: s.quantity,
      invoiceAmount,
      benchmarkAverage: s.marketAverage,
      benchmarkLow: s.marketLow,
      benchmarkHigh: s.marketHigh,
      supportedAmount: s.expectedAmount,
      benchmarkStatus: s.status,
      recoveryStatus,
      eligible,
      evidenceBasis: s.evidenceBasis,
      formula: eligible
        ? `vendor ${invoiceAmount ?? 0} − (benchmark ${s.marketAverage ?? 0} × qty ${s.quantity})`
        : `no recovery — ${CHARGE_RECOVERY_STATUS_LABEL[recoveryStatus]}`,
      recoveryAmount: contribution,
    };
  });

  const eligibleLines = lines.filter((l) => l.eligible);
  const supportedVendorTotal = round2(
    eligibleLines.reduce((sum, l) => sum + (l.invoiceAmount ?? 0), 0),
  );
  const supportedExpectedTotal = round2(
    eligibleLines.reduce((sum, l) => sum + (l.supportedAmount ?? 0), 0),
  );
  const rawRecovery = round2(supportedVendorTotal - supportedExpectedTotal);
  const supportedRecovery = clampMoney(rawRecovery, invoiceTotal);

  /* Deterministic fair-payment band. The upper recovery bound prices the
     supported scope at each benchmark's documented LOW bound, the lower bound
     at its HIGH bound. Missing bounds fall back to the selected benchmark, so
     the band collapses onto the point estimate rather than being invented. */
  const boundRecovery = (pick: (l: ChargeRecoveryLine) => number | null): number => {
    let sum = 0;
    for (const l of eligibleLines) {
      const perUnit = pick(l) ?? l.benchmarkAverage;
      if (perUnit === null || l.invoiceAmount === null) continue;
      sum += l.invoiceAmount - perUnit * l.quantity;
    }
    return round2(sum);
  };
  const recoveryAtLowBenchmark = clampMoney(boundRecovery((l) => l.benchmarkLow), invoiceTotal);
  const recoveryAtHighBenchmark = clampMoney(boundRecovery((l) => l.benchmarkHigh), invoiceTotal);

  const fairPayment = invoiceTotal !== null ? clampMoney(invoiceTotal - supportedRecovery, invoiceTotal) : null;
  let fairPaymentLow: number | null = null;
  let fairPaymentHigh: number | null = null;
  if (invoiceTotal !== null && eligibleLines.length > 0) {
    const a = clampMoney(invoiceTotal - recoveryAtLowBenchmark, invoiceTotal);
    const b = clampMoney(invoiceTotal - recoveryAtHighBenchmark, invoiceTotal);
    fairPaymentLow = Math.min(a, b);
    fairPaymentHigh = Math.max(a, b);
  }

  return {
    supportedRecovery,
    supportedVendorTotal,
    supportedExpectedTotal,
    invoiceTotal,
    fairPayment,
    fairPaymentLow,
    fairPaymentHigh,
    lines,
    counts: {
      reviewed: lines.length,
      eligible: eligibleLines.length,
      supportedRecovery: lines.filter((l) => l.recoveryStatus === "supported_recovery").length,
      additionalEvidenceRequired: lines.filter(
        (l) => l.recoveryStatus === "additional_evidence_required",
      ).length,
      vendorClarificationRequired: lines.filter(
        (l) =>
          l.recoveryStatus === "vendor_clarification_required" ||
          l.recoveryStatus === "duplicate_charge",
      ).length,
    },
    empty: lines.length === 0,
    calculationBasis: "deterministic_charge_level",
    calculationVersion: RECOVERY_CALCULATION_VERSION,
  };
}

/**
 * Stable, order-independent hash of the deterministic result. Two runs over
 * the same canonical inputs must produce the same digest.
 */
export function recoveryFingerprint(result: DeterministicRecoveryResult): string {
  const payload = result.lines
    .map((l) =>
      [
        l.chargeId,
        l.normalizedChargeKey,
        l.quantity,
        l.invoiceAmount ?? "",
        l.benchmarkAverage ?? "",
        l.supportedAmount ?? "",
        l.recoveryStatus,
        l.recoveryAmount,
      ].join("|"),
    )
    .sort()
    .join("\n");
  return `${result.calculationVersion}:${stableHash(
    `${payload}\n${result.supportedRecovery}\n${result.fairPaymentLow}\n${result.fairPaymentHigh}`,
  )}`;
}

/** FNV-1a — deterministic, dependency-free, runs identically on every runtime. */
export function stableHash(input: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 + c + i, 0x85ebca6b) >>> 0;
  }
  return `${h1.toString(16).padStart(8, "0")}${h2.toString(16).padStart(8, "0")}`;
}
